import {
  makePercent,
  Percent,
  PERCENT_DECIMALS,
  PERCENT_ONE_HUNDRED,
  PERCENT_ZERO,
  PercentFromBigInt,
} from "@silo/types";
import { BigInt, flow, pipe, Schedule } from "effect";
import { formatUnits } from "viem";
import { parseNumber, parsePercent, trimNegativeZero } from "./string";
import { shiftDecimal } from "./bigint";
import { clamp as clampBigint } from "effect/BigInt";
import { clamp as clampNumber } from "effect/Number";
import { Schema } from "@effect/schema";

export type PercentFromPointsString = typeof PercentFromPointsString.Type;
export type PercentFromPointsStringEncoded =
  typeof PercentFromPointsString.Encoded;

/**
 * `"4.2808792775904"` => Percent representing 4.3%
 */
export const PercentFromPointsString = Schema.transform(
  Schema.String,
  PercentFromBigInt,
  {
    decode: (value) => {
      return pipe(value, parsePercent, BigInt.unsafeDivide(100n), makePercent);
    },
    encode: (value) => {
      return pipe(value, BigInt.multiply(100n), (value) =>
        formatUnits(value, PERCENT_DECIMALS)
      );
    },
  }
).annotations({ identifier: "PercentFromPointsString" });

/**
 * Formats a Percent with provided `digits` (1 by default)
 */
export function asPercent(value: Percent, digits = 1): string {
  const fixedPercentFormatter = new Intl.NumberFormat("en-US", {
    style: "percent",
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });

  return pipe(
    formatUnits(value, PERCENT_DECIMALS),
    parseNumber,
    (num) => fixedPercentFormatter.format(num),
    trimNegativeZero
  );
}

export function asPercentValue(value: Percent, digits?: number): Percent {
  return pipe(
    asPercent(value, digits),
    parsePercent,
    (percent) => shiftDecimal(percent, -2),
    makePercent
  );
}

/**
 * Formats a Percent as a whole number.
 */
export function asWholePercent(value: Percent): string {
  return asPercent(value, 0);
}

export function asWholePercentValue(value: Percent): Percent {
  return asPercentValue(value, 0);
}

const exactPercentFormatter = new Intl.NumberFormat("en-US", {
  style: "percent",
  maximumSignificantDigits: PERCENT_DECIMALS,
});

export function asExactPercent(value: Percent): string {
  return pipe(
    formatUnits(value, PERCENT_DECIMALS),
    parseNumber,
    (num) => exactPercentFormatter.format(num),
    trimNegativeZero
  );
}

export function asExactPercentagePoints(value: Percent): string {
  return pipe(shiftDecimal(value, 2), (_value) =>
    formatUnits(_value, PERCENT_DECIMALS)
  );
}

export function toPercentagePoints(value: Percent): number {
  return pipe(
    formatUnits(value, PERCENT_DECIMALS),
    parseNumber,
    (percent) => percent * 100
  );
}

export function toWholePercentagePoints(value: Percent): number {
  return pipe(value, toPercentagePoints, Math.round);
}

interface ClampPercentParams {
  minimum?: Percent;
  maximum?: Percent;
}

export const clampPercent =
  (params?: ClampPercentParams) => (value: Percent) => {
    const minimum = params?.minimum ?? PERCENT_ZERO;
    const maximum = params?.maximum ?? PERCENT_ONE_HUNDRED;

    return pipe(value, clampBigint({ minimum, maximum }), makePercent);
  };

export function clampPoints(points: number): number {
  return pipe(points, clampNumber({ minimum: 0, maximum: 100 }));
}

/**
 * Converts a `Percent` to points, rounds to whole number, and clamps between 0 and 100.
 */
export const normalizeToPoints = flow(
  toPercentagePoints,
  Math.round,
  clampPoints
);

export const normalizeToPoints_LEGACY = flow(makePercent, normalizeToPoints);
